import { phoneCountries } from "../data/phoneCountries";

const DEFAULT_ISO2 = "US";

const normalizeDigits = (value = "") => String(value || "").replace(/\D/g, "");

const buildFlagUrl = (iso2 = DEFAULT_ISO2) => `https://flagcdn.com/w40/${String(iso2 || DEFAULT_ISO2).toLowerCase()}.png`;

const countryCatalog = (phoneCountries || []).map((country) => ({
	...country,
	iso2: String(country?.code || "").toUpperCase(),
	dialCode: normalizeDigits(country?.phone || ""),
}));

const DEFAULT_COUNTRY =
	countryCatalog.find((country) => country.iso2 === DEFAULT_ISO2) || countryCatalog[0] || { iso2: DEFAULT_ISO2, dialCode: "1", label: "United States" };

const countriesByDialLengthDesc = [...countryCatalog]
	.filter((country) => country.dialCode)
	.sort((a, b) => b.dialCode.length - a.dialCode.length);

export const getDefaultPhoneCountry = () => DEFAULT_COUNTRY;

export const findPhoneCountryByIso2 = (iso2 = "") => {
	const normalizedIso = String(iso2 || "").trim().toUpperCase();
	if (!normalizedIso) return null;
	return countryCatalog.find((country) => country.iso2 === normalizedIso) || null;
};

export const findPhoneCountryByLabel = (label = "") => {
	const normalizedLabel = String(label || "").trim().toLowerCase();
	if (!normalizedLabel) return null;
	return countryCatalog.find((country) => String(country?.label || "").trim().toLowerCase() === normalizedLabel) || null;
};

export const findPhoneCountryByDialCode = (dialCode = "") => {
	const digits = normalizeDigits(dialCode);
	if (!digits) return null;
	// For NANP shared code (+1), default to US unless a stronger signal exists (label/iso2).
	if (digits === "1") {
		return findPhoneCountryByIso2("US") || null;
	}
	return countryCatalog.find((country) => country.dialCode === digits) || null;
};

export const detectPhoneCountryFromValue = (value = "", fallbackCountry = null) => {
	const digits = normalizeDigits(value);
	if (!digits) return fallbackCountry || DEFAULT_COUNTRY;

	const preferred = fallbackCountry && digits.startsWith(fallbackCountry.dialCode) ? fallbackCountry : null;
	if (preferred) return preferred;

	const detected = countriesByDialLengthDesc.find((country) => digits.startsWith(country.dialCode));
	return detected || fallbackCountry || DEFAULT_COUNTRY;
};

export const resolvePhoneCountry = ({ iso2, dialCode, label, fallbackIso2 = DEFAULT_ISO2 } = {}) => {
	return (
		findPhoneCountryByIso2(iso2) ||
		findPhoneCountryByLabel(label) ||
		findPhoneCountryByDialCode(dialCode) ||
		findPhoneCountryByIso2(fallbackIso2) ||
		DEFAULT_COUNTRY
	);
};

export const normalizePhoneForCountry = (value = "", countryInput = null, maxLocalDigits = 10) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;

	const digits = normalizeDigits(value);
	if (!digits) return `+${country.dialCode}`;

	let localDigits = digits;
	const detectedCountry = detectPhoneCountryFromValue(value, country);
	if (detectedCountry?.dialCode && localDigits.startsWith(detectedCountry.dialCode)) {
		localDigits = localDigits.slice(detectedCountry.dialCode.length);
	} else if (country?.dialCode && localDigits.startsWith(country.dialCode)) {
		localDigits = localDigits.slice(country.dialCode.length);
	}

	localDigits = localDigits.slice(0, maxLocalDigits);
	return localDigits ? `+${country.dialCode} ${localDigits}` : `+${country.dialCode}`;
};

export const getNationalDigits = (value = "", countryInput = null) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;
	const digits = normalizeDigits(value);
	if (!digits) return "";
	if (country?.dialCode && digits.startsWith(country.dialCode)) {
		return digits.slice(country.dialCode.length);
	}
	return digits;
};

export const getPhoneMetaForCountry = (countryInput = null) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;
	return {
		phone_country_code: country.dialCode,
		phone_country_iso2: country.iso2,
		phone_country_flag: buildFlagUrl(country.iso2),
	};
};

export const resolvePhoneState = ({
	phoneValue,
	countryLabel,
	phoneCountryIso2,
	phoneCountryCode,
	fallbackIso2 = DEFAULT_ISO2,
} = {}) => {
	const resolvedCountry = resolvePhoneCountry({
		iso2: phoneCountryIso2,
		dialCode: phoneCountryCode,
		label: countryLabel,
		fallbackIso2,
	});

	return {
		country: resolvedCountry,
		phoneValue: normalizePhoneForCountry(phoneValue || "", resolvedCountry),
		meta: getPhoneMetaForCountry(resolvedCountry),
	};
};

export { buildFlagUrl, normalizeDigits };
